import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const COUNTDOWN_COLOR_CLASSES = ['text-green-400', 'text-yellow-400', 'text-red-500'];
const TIME_THRESHOLDS = {
    GREEN: 600,  // More than 10 minutes
    YELLOW: 120  // Between 2 and 10 minutes
};

/**
 * Formats remaining duration in human-friendly format
 * @param {number} seconds - Remaining time in seconds
 * @returns {string} Formatted duration string (e.g., "5h 30m 15s")
 */
function formatDuration(seconds) {
    const d = dayjs.duration(seconds, 'seconds');
    const parts = [];

    if (d.hours() > 0) parts.push(`${d.hours()}h`);
    if (d.minutes() > 0) parts.push(`${d.minutes()}m`);
    if (d.seconds() > 0 || parts.length === 0) parts.push(`${d.seconds()}s`);

    return parts.join(' ');
}

/**
 * Updates countdown text color based on remaining time
 * Uses Tailwind CSS utility classes
 * @param {HTMLElement} element - The countdown element
 * @param {number} remainingSeconds - Remaining time in seconds
 */
function updateCountdownColor(element, remainingSeconds) {
    element.classList.remove(...COUNTDOWN_COLOR_CLASSES);

    const colorClass = remainingSeconds > TIME_THRESHOLDS.GREEN
        ? 'text-green-400'
        : remainingSeconds > TIME_THRESHOLDS.YELLOW
            ? 'text-yellow-400'
            : 'text-red-500';

    element.classList.add(colorClass);
}

/**
 * Initializes a countdown timer for a specific element
 * Element must have data-remaining attribute with seconds value
 * @param {HTMLElement} element - The countdown element
 */
function initCountdown(element) {
    let remainingSeconds = parseInt(element.dataset.remaining, 10);
    if (isNaN(remainingSeconds)) return;

    updateCountdownDisplay(element, remainingSeconds);

    const intervalId = setInterval(() => {
        remainingSeconds--;

        dispatchCountdownTick(element, remainingSeconds);

        if (remainingSeconds <= 0) {
            handleCountdownEnd(element, intervalId);
            return;
        }

        updateCountdownDisplay(element, remainingSeconds);
    }, 1000);
}

/**
 * Updates countdown display text and color
 */
function updateCountdownDisplay(element, remainingSeconds) {
    element.textContent = formatDuration(remainingSeconds);
    updateCountdownColor(element, remainingSeconds);
}

/**
 * Dispatches countdown tick event for Alpine.js
 */
function dispatchCountdownTick(element, remainingSeconds) {
    element.dispatchEvent(
        new CustomEvent("countdown-tick", {
            bubbles: true,
            detail: {remaining: remainingSeconds}
        })
    );
}

/**
 * Handles countdown end state
 */
function handleCountdownEnd(element, intervalId) {
    element.textContent = "0s";
    element.classList.remove(...COUNTDOWN_COLOR_CLASSES);
    element.classList.add("text-red-500");
    clearInterval(intervalId);
}

/**
 * Init count down
 */
export function init(parent) {
    parent.querySelectorAll('.js-countdown').forEach(initCountdown);
}
